import { UserConstants } from "@@types/IUser";
import { AuthContext } from "@contexts/auth/AuthContext";
import { useDisclosure } from "@kirvanobr/use-disclosure";
import { useGetCompanies } from "@queries/companies";
import { useFetchMe } from "@queries/user";
import { useRouter } from "next/router";
import { useCallback, useContext, useEffect } from "react";
import { MarketPlaceBannerUseCasesType } from "./BannerTop.types";
import { Buyer } from "./components/ModalContents/Buyer/Buyer";
import { NotFinishedOnboard } from "./components/ModalContents/NotFinishedOnboard/NotFinishedOnboard";
import { SellerWithProducts } from "./components/ModalContents/SellerWithProducts/SellerWithProducts";

export const useBannerTop = () => {
  const { setShowBanner, becomeSeller, showBanner } = useContext(AuthContext);
  const { data: user, isLoading: userLoading, isFetching: userFetching } = useFetchMe();
  const { data: companies } = useGetCompanies({});

  const { isOpen, onOpenChange, onOpen } = useDisclosure();
  const router = useRouter();
  const isUserLoading = userLoading || userFetching;

  const requirementsHelpUrl =
    "https://help.kirvano.com/pt-BR/articles/8871870-como-liberar-meu-produto-para-afiliacao#h_92585f5b85";
  const isProfileUpdated = !!user?.isUpdated;
  const hasCompany = !!companies?.data.data.length;
  const isSellerCompleteProfile = isProfileUpdated && hasCompany;

  const handleRedirection = (page: string) => {
    router.push(page);
  };

  const bannerUseCasesOptions = useCallback((): MarketPlaceBannerUseCasesType | undefined => {
    if (isUserLoading) return;

    if (user && user?.type === UserConstants.Type.BUYER) {
      return "isBuyer";
    }

    if (user && user.type === UserConstants.Type.SELLER && isSellerCompleteProfile) {
      return "seller";
    }

    return "sellerIncompleteOnboarding";
  }, [user, isSellerCompleteProfile, isUserLoading]);

  function bannerModalUseCasesRender() {
    switch (bannerUseCasesOptions()) {
      case "isBuyer":
        return <Buyer />;

      case "seller":
        return <SellerWithProducts />;

      case "sellerIncompleteOnboarding":
        return <NotFinishedOnboard />;
      default:
        return <SellerWithProducts />;
    }
  }

  const shouldDisplayBanner = useCallback(() => {
    if (bannerUseCasesOptions() === "isBuyer" && router.pathname.includes("purchases")) {
      return true;
    }

    if (bannerUseCasesOptions() !== "isBuyer" && router.pathname.includes("dashboard")) {
      return true;
    }

    return false;
  }, [bannerUseCasesOptions, router.pathname]);

  useEffect(() => {
    setShowBanner(false);
    if (shouldDisplayBanner()) setShowBanner(true);
  }, [bannerUseCasesOptions, router.pathname, setShowBanner, user, shouldDisplayBanner]);

  return {
    isOpen,
    onOpenChange,
    onOpen,
    requirementsHelpUrl,
    becomeSeller,
    handleRedirection,
    shouldDisplayBanner,
    bannerUseCasesOptions,
    bannerModalUseCasesRender,
  };
};
